import {
  Component,
  ChangeDetectionStrategy,
  Input,
  ViewChildren,
  QueryList,
  Output,
  EventEmitter,
  ChangeDetectorRef,
  AfterViewInit,
  OnChanges,
} from '@angular/core';
import { BehaviorSubject, Observable, combineLatest, from, map, of, tap } from 'rxjs';
import pdfjsLib from 'src/assets/pdfjs-config';
import { FLAGS, FlagsService } from 'src/app/services/flags.service';
import { SessionSharedDataService } from 'src/app/services/session-shared-data.service';
import { ItemsCanvasService } from 'src/app/services/items-canvas.service';
import { PdfPageComponent } from 'src/app/standalones/components/pdf-viewer/pdf-page/pdf-page.component';
import { PDFPageProxy } from 'pdfjs-dist';
import { UntilDestroy } from '@ngneat/until-destroy';
import { SpaceZoomCutoffService } from '../../../../services/space-zoom-cutoff.service';
import { DeviceAndBrowserDetectorService } from '../../../../services/device-and-browser-detector.service';
import { PDFConfigurations } from '../../items-canvas/board-items/board-pdf-configurations';
import { PdfCacheService } from '../../../../services/pdf-cache.service';
import { ResourceItemModel } from '../../../../models/resource';
import { UserService } from '../../../../services/user.service';
import { PageWithViewport } from '../pdf-services/pdf-manager.service';

export const MAX_PDF_PAGES_TO_RENDER = 500;
const MIN_PAGE_ENDER_ZOOM_LEVEL = 0.25;
export const PDF_VIEWPORT_SCALE_FACTOR = 1.4;

interface PdfPageDimensions {
  height: number;
  width: number;
}

@UntilDestroy()
@Component({
  selector: 'app-pdf-viewer[canvasItem]',
  templateUrl: './pdf-viewer.component.html',
  styleUrls: ['./pdf-viewer.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PdfViewerComponent implements AfterViewInit, OnChanges {
  @Input() static = false;
  @Input() state!: PDFConfigurations;

  @Output() contentSizeAcquired = new BehaviorSubject<
    { width: number; height: number } | undefined
  >(undefined);
  @Output() stateChanged = new EventEmitter<PDFConfigurations>();

  pdfLoaded = false;

  showUploadIndicator$ = new BehaviorSubject(false);

  constructor(
    private flagsService: FlagsService,
    private sharedDataService: SessionSharedDataService,
    private cdRef: ChangeDetectorRef,
    private itemsCanvasService: ItemsCanvasService,
    private spaceZoomCutoffService: SpaceZoomCutoffService,
    private deviceAndBrowserDetectorService: DeviceAndBrowserDetectorService,
    private pdfCacheService: PdfCacheService,
    private userService: UserService,
  ) {
    this.scaleFactor = this.flagsService.isFlagEnabled(FLAGS.ENABLE_PDF_UPSCALE)
      ? (this.flagsService.featureFlagsVariables.enable_pdf_upscale.pdf_scale_factor as number)
      : 1.0;
  }

  @ViewChildren('pdfPage') vPdfPages?: QueryList<PdfPageComponent>;

  isLoading = new BehaviorSubject<boolean>(true);
  loadingData = new BehaviorSubject<boolean>(false);

  pdfPages: Observable<PageWithViewport>[] = [];

  viewModel$ = of({
    hidePage: this.zoomLevel < MIN_PAGE_ENDER_ZOOM_LEVEL,
    pdfName: '',
    loading: false,
  });

  scaleFactor = 1.0;

  getPdfSource(contentId: string, url: string): Promise<string> {
    return this.itemsCanvasService.getTrustedUrl(url);
  }

  ngOnChanges(changes) {
    // to load the pdf in case the input changed, and now we have the url
    this.loadPDF();
  }

  ngAfterViewInit(): void {
    this.showUploadIndicator$.next(this.state.ownerId === this.userService.userId);

    // to load the pdf as soon as the component init in case we have the url
    this.loadPDF();
  }

  async loadPDF() {
    if (this.static) {
      return;
    }
    if (this.pdfLoaded) {
      return;
    }

    const contentId = this.state.contentId;
    const url = this.state.url;
    if (!contentId || !url) {
      return;
    }

    const cachedPDFProxy = this.pdfCacheService.getPDFIfExists(contentId);
    if (cachedPDFProxy) {
      this.initPDFViewModel();
      this.initPages(url, contentId);
    } else {
      try {
        this.loadingData.next(true);

        const pdfResourceUrl = await this.getPdfSource(contentId, url);

        const pdfDocumentProxy = await pdfjsLib.getDocument({
          url: pdfResourceUrl,
          cMapUrl: `//cdn.jsdelivr.net/npm/pdfjs-dist@${pdfjsLib.version}/cmaps/`,
          cMapPacked: true,
        }).promise;

        this.pdfCacheService.cachePDF(contentId, pdfDocumentProxy);

        this.initPDFViewModel();
        this.initPages(url, contentId);

        this.loadingData.next(false);
      } catch (e) {
        // TODO <Hatahet> report not being able to load the pdf here to telemetry
      }
    }
  }

  initPDFViewModel(): void {
    this.viewModel$ = combineLatest([
      this.spaceZoomCutoffService.shouldCutOff$,
      of(this.state.name),
      this.isLoading.asObservable(),
    ]).pipe(
      tap(() => {
        if (!this.hidePage && !this.isLoading.getValue() && this.pdfPages.length === 0) {
          this.isLoading.next(true);
        }
      }),
      map(([hidePage, pdfName, loading]) => ({
        hidePage,
        pdfName,
        loading,
      })),
    );
  }

  initPages(url: string, contentId: string): void {
    if (!this.canInitPage || !contentId) {
      return;
    }

    const pdf = this.pdfCacheService.getPDFIfExists(contentId);
    if (!pdf) {
      return;
    }

    this.isLoading.next(true);

    const numberOfPagesToRender = Math.min(pdf.numPages as number, MAX_PDF_PAGES_TO_RENDER);
    this.pdfPages = Array.from({ length: numberOfPagesToRender }, (_, pageIndex) => {
      const cachedPage = this.pdfCacheService.getPageIfExists(contentId, pageIndex);

      if (cachedPage) {
        const dimensions = {
          height: cachedPage.height,
          width: cachedPage.width,
        };
        this.updateContentSize(dimensions, numberOfPagesToRender);
        return of({
          pageProxy: cachedPage.pageProxy,
          viewport: cachedPage.viewport,
          height: dimensions.height,
          width: dimensions.width,
        } as PageWithViewport);
      }

      return from(pdf.getPage(pageIndex + 1)).pipe(
        map((page) => this.processNewPage(page, contentId, pageIndex, numberOfPagesToRender)),
      );
    });

    this.pdfLoaded = true;
  }

  get scaleForPdfPages(): number {
    if (this.deviceAndBrowserDetectorService.isiOSiPhone()) {
      return (
        (this.flagsService.featureFlagsVariables.enable_pdf_upscale
          .pdf_scale_factor_mobile_ios as number) || 1
      );
    }
    if (this.deviceAndBrowserDetectorService.isAndroid()) {
      return (
        (this.flagsService.featureFlagsVariables.enable_pdf_upscale
          .pdf_scale_factor_mobile_android as number) || 1.2
      );
    }
    return this.scaleFactor * PDF_VIEWPORT_SCALE_FACTOR;
  }

  onPageLoaded(): void {
    this.isLoading.next(false);
    this.cdRef.detectChanges();
  }

  get zoomLevel(): number {
    return this.sharedDataService.fabricCanvas?.getZoom() || 1;
  }

  get hidePage(): boolean {
    return this.zoomLevel < MIN_PAGE_ENDER_ZOOM_LEVEL;
  }

  get canInitPage(): boolean {
    return this.pdfPages.length === 0 && !this.static;
  }

  protected readonly ResourceItemModel = ResourceItemModel;

  private calculatePageDimensions(viewport: pdfjsLib.PageViewport): PdfPageDimensions {
    return {
      width: Math.floor(viewport.width / this.scaleFactor),
      height: Math.floor(viewport.height / this.scaleFactor),
    };
  }

  private updateContentSize(dimensions: PdfPageDimensions, totalPages: number): void {
    this.contentSizeAcquired.next({
      height: dimensions.height * totalPages,
      width: dimensions.width,
    });
  }

  private processNewPage(
    page: PDFPageProxy,
    contentId: string,
    pageIndex: number,
    totalPages: number,
  ): PageWithViewport {
    const viewport = page.getViewport({ scale: this.scaleForPdfPages });
    const dimensions = this.calculatePageDimensions(viewport);

    this.pdfCacheService.cachePage(contentId, pageIndex, {
      pageProxy: page,
      viewport,
      ...dimensions,
    });

    this.updateContentSize(dimensions, totalPages);

    return {
      pageProxy: page,
      viewport,
      height: dimensions.height,
      width: dimensions.width,
    };
  }
}
